
import {
  computed, defineComponent, inject, onMounted, onUnmounted, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDropZone } from '@vueuse/core';
// eslint-disable-next-line import/no-cycle
import { useMessagesStore } from '../store/messagesStore';
import MessageItem from '../components/MessageItem.vue';
import MessageMockItem from '../components/MessageMockItem.vue';
import AppMessageInput from '@/components/UI/AppMessageInput.vue';
// eslint-disable-next-line import/no-cycle
import WebsocketService from '@/services/WebsocketService';
// eslint-disable-next-line
import { CONTACT_STATUS, MESSAGE_CATEGORY, NAVIGATION_PAGES, UPLOAD_PLACES } from '@/types/enums';
// eslint-disable-next-line
import { IConversationItemDirectList, IMockMessage, IImageCrop } from '@/types/interfaces';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import AppSkeletonMessageMedia from '../../../components/skeletons/AppSkeletonMessageMedia.vue';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
import SkeletonService from '@/services/SkeletonService';
import AppSkeletonDialog from '@/components/skeletons/AppSkeletonDialog.vue';
// eslint-disable-next-line import/no-cycle
import NativeService from '@/services/NativeService';
import isPhoto from '@/helpers/fileDetector';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '@/modules/drive/store/driveStore';
// eslint-disable-next-line import/no-cycle
import UploadService from '@/services/UploadService';
// eslint-disable-next-line import/no-cycle
import MessagesService from '@/modules/messages/services/MessagesService';
import AppNoMessage from '@/components/AppNoMessage.vue';
import UseUpload from '@/composables/UseUpload';
import AppDropZone from '@/components/AppDropZone.vue';
// import { ElLoading } from 'element-plus'

export default defineComponent({
  components: {
    MessageItem,
    MessageMockItem,
    AppMessageInput,
    AppSkeletonMessageMedia,
    AppSkeletonDialog,
    AppNoMessage,
    AppDropZone,
  },
  props: {
    isGroup: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  setup(props) {
    /** The chat container ".dialog__list" is rotated vertically to make scrolling smoother */
    /** Each "dialog__item" message is rotated to its original orientation so that the text is displayed correctly */
    /** Note that this also reverses the orientation: top becomes bottom, and vice versa */

    const fullscreenLoading = ref(false);
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;
    const messagesStore = useMessagesStore();
    const mainStore = useMainStore();
    const route = useRoute();
    const router = useRouter();

    const driveStore = useDriveStore();
    const { onUploadUpdate } = UseUpload();
    const currentPage = computed(() => route.meta.navigationPage);

    const { currentDialogsSkeletons } = SkeletonService.getSkeletonInfo();

    const dataLoading = ref<boolean>(true);
    const scrollItem = ref<HTMLDivElement | null>(null);
    const messagesLoading = ref<boolean>(false);
    const hasMessages = computed(() => messagesStore.currentConversation?.messages?.length);

    const timeScroller = ref<null | HTMLDivElement>(null);
    let navigationX = 0;
    let navigationY = 0;
    let lastNavigationY = 0;

    const contactId = computed(() => route.params.id as string);

    const mockMessages = computed(() => messagesStore.mockMessages.find((list) => list.key === contactId.value)?.value || []);

    const showGoBottom = ref(false);
    const mediaLoadedKey = ref(0);
    const mediaToCrop = ref({} as IImageCrop);
    const mediaInputRef = ref();
    const mediaUploading = ref(false);
    const dialogId = ref(route.params.id);

    const messages = computed(() => messagesStore.currentConversation?.messages
      .filter((v, i, a) => a.findIndex((v2) => (v2.id === v.id)) === i).reverse() || []);
    const skeletons = computed(() => {
      if (messagesStore.currentConversation?.info.id) {
        const val = currentDialogsSkeletons[messagesStore.currentConversation.info.id];
        return val || 0;
      }
      return 0;
    });

    function sendMessage(message: string) {
      if (!message) {
        return;
      }
      WebsocketService.sendMessage(contactId.value, message);
    }

    function resendMessage(mockMsg: IMockMessage) {
      messagesStore.removeMockMessage(contactId.value, mockMsg.uuid);
      sendMessage(mockMsg.text);
    }

    function scrollToBottom(behaviorSmooth?: boolean): void {
      setTimeout(() => {
        if (scrollItem.value) {
          scrollItem.value.scrollTo({
            top: 0,
            behavior: behaviorSmooth ? 'smooth' : 'auto',
          });
        }
      }, 100);
    }

    function scrollToFirstUnread(): void {
      // TODO possible Sentry CASEDRIVE-G issue - if currentConversation undefined ???
      if (messagesStore.currentConversation?.messages?.length) {
        const idx = messagesStore.currentConversation.messages.findIndex(
          (msg) => msg.owner.accountId === messagesStore.currentConversation!.info.id && !msg.read,
        );
        const msg = document.querySelectorAll('.msg-item')[idx];
        if (msg) {
          msg.scrollIntoView({ block: 'center', inline: 'nearest' });
        } else {
          scrollToBottom();
        }
      }
    }

    function readMessages(): void {
      messagesStore.currentConversation!.messages.forEach((msg) => {
        if (msg.owner.accountId === messagesStore.currentConversation!.info.id && !msg.read) {
          WebsocketService.readMessage(messagesStore.currentConversation!.info.id, msg.content, msg.id);
          // eslint-disable-next-line no-param-reassign
          msg.read = true;
        }
      });
      const currentConv = messagesStore.conversations.find((conv) => conv.conversationId === messagesStore.currentConversation?.conversationId);
      if (currentConv) {
        currentConv.unreadCount = 0;
      }
    }

    function setShowDate(): void {
      if (!messagesStore.currentConversation?.messages?.length) {
        return;
      }

      let previousDate = messagesStore.currentConversation.messages[messagesStore.currentConversation.messages.length - 1]?.created;

      if (!previousDate) {
        return;
      }

      function isDaysEqual(prevDate: Date, msgDate: Date): boolean {
        return prevDate.getFullYear() === msgDate.getFullYear() && prevDate.getMonth() === msgDate.getMonth() && prevDate.getDay() === msgDate.getDay();
      }

      messagesStore.currentConversation.messages.forEach((msg) => {
        const prevDate = new Date(previousDate);
        const msgDate = new Date(msg.created);
        if (!isDaysEqual(prevDate, msgDate)) {
          // eslint-disable-next-line no-param-reassign
          msg.showDate = true;
        } else {
          // eslint-disable-next-line no-param-reassign
          msg.showDate = false;
        }
        previousDate = msg.created;
      });

      messagesStore.currentConversation!.messages[0].showDate = true;
    }

    function onScroll(): void {
      if (scrollItem.value) {
        const { scrollTop } = scrollItem.value;
        showGoBottom.value = scrollTop > 500;
      }
      if (!messagesStore.currentConversation?.previousMessageTimestamp) {
        return;
      }
      if (scrollItem.value) {
        const isTop = (scrollItem.value.scrollTop + scrollItem.value.clientHeight) >= scrollItem.value.scrollHeight - 1000;

        if (isTop && !messagesLoading.value) {
          messagesLoading.value = true;
          messagesStore.fetchConversationById(contactId.value, undefined, true)
            .then(() => {
              setShowDate();
              setTimeout(() => {
                messagesLoading.value = false;
              });
            });
        }
      }
    }

    function formatDate(date: string): string {
      const today = new Date();
      const currentDate = new Date(date);

      const isToday = (
        currentDate.getDate() === today.getDate()
        && currentDate.getMonth() === today.getMonth()
        && currentDate.getFullYear() === today.getFullYear()
      );

      const isThisYear = currentDate.getFullYear() === today.getFullYear();

      if (isToday) {
        return 'Today';
      }
      if (isThisYear) {
        return currentDate.toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'long',
        });
      }
      return currentDate.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }

    function formatTime(time: string) {
      return new Date(time).toLocaleString('en-US', { hour: 'numeric', hour12: false, minute: 'numeric' });
    }

    function elementDraged(event: MouseEvent | TouchEvent) {
      if ((event as MouseEvent)?.clientX) {
        navigationX = (event as MouseEvent)?.clientX;
        navigationY = (event as MouseEvent)?.clientY;
        return;
      }
      if ((event as TouchEvent)?.changedTouches?.length) {
        navigationX = (event as TouchEvent)?.changedTouches[0]?.clientX;
        navigationY = (event as TouchEvent)?.changedTouches[0]?.clientY;
      }
    }

    function mouseMove(event: MouseEvent) {
      let movementX = navigationX - event.clientX;

      if (movementX < 15) {
        movementX = 0;
      }
      if (movementX > 75) {
        movementX = 75;
      }

      if (scrollItem.value) {
        scrollItem.value.scrollLeft = movementX;
      }
    }

    function elementDroped(event: MouseEvent | TouchEvent) {
      navigationY = 0;
      lastNavigationY = 0;
      if ((event as MouseEvent)?.clientX) {
        navigationX = 0;
      }
      if (scrollItem.value) {
        scrollItem.value.scrollLeft = 0;
      }
    }

    function touchMove(event: TouchEvent) {
      let movementX = navigationX - event.changedTouches[0].clientX;
      const movementY = navigationY - event.changedTouches[0].clientY;
      if (movementX < 15) {
        movementX = 0;
      }
      if (movementX > 75) {
        movementX = 75;
      }

      if (movementX < 25 && (lastNavigationY > movementY + 4 || lastNavigationY < movementY - 4)) {
        navigationX = 0;
      }

      if (scrollItem.value) {
        scrollItem.value.scrollLeft = movementX;
      }
      lastNavigationY = movementY;
    }

    function checkQuery() {
      const shareSettings = route.query.shareSettings ? JSON.parse(route.query.shareSettings as string) : null;
      if (route.query.sendCase) {
        WebsocketService.sendCase(contactId.value, route.query.sendCase as string, '', false, shareSettings);
        router.replace({});
      } else if (route.query.sendFile) {
        WebsocketService.sendMedia(contactId.value, route.query.sendFile as string, false, shareSettings);
        router.replace({});
      } else if (route.query.sendFolder) {
        WebsocketService.sendFolder(contactId.value, route.query.sendFolder as string, false);
        router.replace({});
      }
    }

    function onStartUpload() {
      scrollToBottom();
    }

    function blurInput() {
      const input = document.getElementById('input-field');
      if (input) {
        input.blur();
      }
    }
    function splitMedia(mediaList: File[]) {
      const fileList: File[] = [];
      const photoList: File[] = [];

      for (let i = 0; i < mediaList.length; i += 1) {
        if (isPhoto(mediaList[i])) {
          photoList.push(mediaList[i]);
        } else {
          fileList.push(mediaList[i]);
        }
      }

      return {
        fileList,
        photoList,
      };
    }
    function addMedia(e: any) {
      const img: File = (e.target as HTMLInputElement).files![0];
      mediaToCrop.value = {
        url: URL.createObjectURL(img),
        fileName: img.name,
        fileType: img.type,
      };
      mediaLoadedKey.value += 1;

      mediaInputRef.value!.value = '';
    }

    function onDrop(mediaList: File[] | null) {
      if (mediaList) {
        const { fileList, photoList } = splitMedia(mediaList);

        const fileUploads = UploadService.toFileUploads(fileList as unknown as FileList);
        const photoUploads = UploadService.toFileUploads(photoList as unknown as FileList);

        const convId = messagesStore.currentConversation!.info.id;

        if (fileUploads.length) {
          UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.DIALOG, id: convId })
            .then((response) => {
              response.forEach((res) => {
                WebsocketService.sendMedia(
                  convId,
                  res.id,
                  props.isGroup,
                );
              });
            })
            .finally(() => {
              mediaUploading.value = false;
            });
        }
        if (photoUploads.length) {
          UploadService.batchUploadTUS(photoUploads, onUploadUpdate, { location: UPLOAD_PLACES.DIALOG, id: convId })
            .then((response) => {
              response.forEach((res) => {
                WebsocketService.sendMedia(
                  convId,
                  res.id,
                  props.isGroup,
                );
              });
            })
            .finally(() => {
              mediaUploading.value = false;
            });
        }
      }
    }
    const dropZoneRef = ref<HTMLElement>();

    const { isOverDropZone } = useDropZone(dropZoneRef, onDrop);

    watch(() => messagesStore.currentConversation?.messages?.length, (newLength, oldLength) => {
      if (newLength && !messagesLoading.value) {
        scrollToBottom();
        readMessages();
        setShowDate();
      }
    });

    watch(() => mainStore.uploadInfos.length, (newVal, prevVal) => {
      if (!prevVal && newVal) {
        scrollToBottom();
      }
    });

    function fetchConv() {
      const currentConv = messagesStore.conversations.find((conv) => (conv as IConversationItemDirectList)?.partnerId?.internalId === contactId.value);
      console.log('contact id', contactId.value);
      console.log('Unread count', currentConv?.unreadCount);
      messagesStore.fetchConversationById(contactId.value, 25 + (currentConv?.unreadCount || 0))
        .finally(() => {
          setShowDate();
          scrollToFirstUnread();
          readMessages();
          checkQuery();
          WebsocketService.syncChat();
          setTimeout(() => {
            fullscreenLoading.value = false;
            dataLoading.value = false;
          }, 100);
        });
    }

    function reverseScroll(event) {
      event.preventDefault();

      const scrollSpeed = 2; // adjust as desired

      if (scrollItem.value) {
        scrollItem!.value.scrollTop += (event.deltaY * -scrollSpeed);
      }
    }

    watch(() => route.params.id, (value, oldValue) => {
      if (route.params.id && route.meta.navigationPage === NAVIGATION_PAGES.MESSAGES_ID) {
        NativeService.toggleDialogNotifications(oldValue as string, MESSAGE_CATEGORY.DIRECT_MESSAGING, 'on');
        NativeService.toggleDialogNotifications(value as string, MESSAGE_CATEGORY.DIRECT_MESSAGING, 'off');
        dialogId.value = value;
        MessagesService.joinToChat(route.params.id as string);
        NativeService.awakeCallback = fetchConv;
        fetchConv();
        driveStore.resetCurrentCase();
        dataLoading.value = true;
      }
    });

    // watch(() => messagesStore.toBeSync, (newVal, oldVal) => {
    //   console.log('TO_BE_SYNC', newVal);
    //   if (newVal) {
    //     fullscreenLoading.value = true;
    //     const currentConv = messagesStore.conversations.find((conv) => (conv as IConversationItemDirectList)?.partnerId?.internalId === contactId.value);
    //     console.log('contact id', contactId.value);
    //     console.log('Unread count', currentConv?.unreadCount);
    //     NativeService.awakeCallback = fetchConv;
    //     fetchConv();
    //     messagesStore.toBeSync = false;
    //   }
    // }, { immediate: true });

    onMounted(() => {
      NativeService.awakeCallback = fetchConv;
      NativeService.toggleDialogNotifications(route.params.id as string, MESSAGE_CATEGORY.DIRECT_MESSAGING, 'off');
      MessagesService.joinToChat(route.params.id as string);
      MonitoringService.sendPageMountedSpeed();

      fetchConv();
    });

    onUnmounted(() => {
      messagesStore.toBeSync = false;
      messagesStore.currentConversation = null;
      NativeService.awakeCallback = null;
      NativeService.toggleDialogNotifications(dialogId.value as string, MESSAGE_CATEGORY.DIRECT_MESSAGING, 'on');
      MessagesService.leftFromCurrentChat();
    });

    return {
      fullscreenLoading,
      messages,
      skeletons,
      dataLoading,
      messagesStore,
      scrollItem,
      messagesLoading,
      hasMessages,
      timeScroller,
      currentDialogsSkeletons,
      // uploadSkeleton,
      showGoBottom,
      mockMessages,
      mainStore,
      sendMessage,
      resendMessage,
      formatDate,
      onScroll,
      elementDraged,
      mouseMove,
      touchMove,
      elementDroped,
      formatTime,
      onStartUpload,
      scrollToBottom,
      blurInput,
      CONTACT_STATUS,
      isOverDropZone,
      onDrop,
      splitMedia,
      dropZoneRef,
      addMedia,
      mediaToCrop,
      mediaInputRef,
      mediaUploading,
      reverseScroll,
    };
  },
});
